import React, { useEffect, useState } from "react";
import { useActor } from "@xstate/react";
import { Loader } from "@pushpress/shared-components";
import { useSignupQuery } from "../../../../apis/authApi.hooks";
import { useGenerateCodeQuery } from "../../../../apis/loginApi.hooks";
import { formatHubspotMultiSelectOptions, formatUrl } from "../../utils";
import { getIso2CodeFromCountry } from "../../../../utils/countries";
import { getGymTypeFromSubtype } from "../../../../utils/gymTypes";
import { GymSubtype, GymType } from "../../../../constants/gymSubtypes";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { useAnalytics } from "../../../../components/providers/AnalyticsProvider";
import { SignUpEvent } from "../../constants/signup.events";
import { useSignUpMachine } from "../../context/SignUpMachineContext";
import { SignUpFormField } from "../../constants/signup-form-field.enum";

const getFullPayload = (currentFormData: any) => {
  const gymSubtype: GymSubtype = String(
    currentFormData[SignUpFormField.Vertical],
  ) as GymSubtype;
  const companyName = String(
    currentFormData[SignUpFormField.Company]?.label?.split(",")[0] || "",
  ).trim();
  const firstName = String(
    currentFormData[SignUpFormField.FirstName] || "",
  ).trim();
  const lastName = String(
    currentFormData[SignUpFormField.LastName] || "",
  ).trim();
  const email = String(currentFormData[SignUpFormField.Email] || "").trim();
  const phoneNumber =
    currentFormData[SignUpFormField.Phone] ||
    currentFormData["phoneNumber"] ||
    "";
  const websiteUrl = formatUrl(currentFormData[SignUpFormField.Url] || "");
  const country = currentFormData[SignUpFormField.Country];
  const gymType = getGymTypeFromSubtype(gymSubtype) as GymType;
  const yearsOpen = currentFormData[SignUpFormField.YearsOpen];
  const monthlyRevenue = currentFormData[SignUpFormField.Revenue];
  const businessNeeds = formatHubspotMultiSelectOptions(
    currentFormData[SignUpFormField.BusinessNeeds] || [],
  );
  const currentSoftware =
    currentFormData[SignUpFormField.CurrentSoftware] || "";
  const utmSource = Cookies.get("utm_source") || "";
  const utmMedium = Cookies.get("utm_medium") || "";
  const utmCampaign = Cookies.get("utm_campaign") || "";
  const utmContent = Cookies.get("utm_content") || "";
  const utmTerm = Cookies.get("utm_term") || "";

  const signupPayload = {
    client: {
      company: companyName,
      averageMonthlyRevenue: monthlyRevenue,
      gymType: gymType,
      gymSubtype: gymSubtype,
      url: websiteUrl,
      currentSoftware,
      yearsOpen,
      /*
      Legacy parts of the system require address,
      but none of the fields except for country are used,
      we can pass in empty strings for the other fields
      */
      address: {
        city: "",
        state: "",
        addressLine1: "",
        postalCode: "",
        country: getIso2CodeFromCountry(country),
      },
      businessNeeds: businessNeeds.split(";").map((need) => ({ value: need })),
    },
    user: { email, phone: String(phoneNumber), firstName, lastName },
    meta: {
      plan: { id: "pp-prod-core-free" as const, cancelAtPeriodEnd: 0 as const },
      utm: {
        source: utmSource,
        medium: utmMedium,
        campaign: utmCampaign,
        content: utmContent,
        term: utmTerm,
      },
    },
  };
  return signupPayload;
};

const ImplicitCreateFreeAccountStep: React.FC = () => {
  const machine = useSignUpMachine();
  const [state, send] = useActor(machine);
  const { formData } = state.context;

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  const signup = useSignupQuery();
  const generateCode = useGenerateCodeQuery();
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    const createAccount = async () => {
      try {
        const signupPayload = getFullPayload(formData);

        // Check for required fields
        const email = signupPayload.user.email;
        if (!email) {
          setStatus("error");
          setErrorMessage("Email is required to create your account");
          return;
        }

        // Create the account
        const signupResult = await signup.mutateAsync(signupPayload);

        if (signupResult.uuid) {
          send({ type: "SET_CLIENT_UUID", uuid: signupResult.uuid });
        } else {
          throw new Error("No uuid returned from signup");
        }

        if (signupResult.subdomain) {
          send({ type: "SET_SUBDOMAIN", subdomain: signupResult.subdomain });
        } else {
          throw new Error("No subdomain returned from signup");
        }

        if (signupResult.email) {
          await generateCode.mutateAsync({ email: signupResult.email });
          trackEvent(SignUpEvent.FreeSignupSubmitted, {
            email: signupResult.email,
            company: signupPayload.client.company,
          });

          setStatus("success");
          // Move to the next step - account confirmation
          send({ type: "NEXT" });
        } else {
          throw new Error("No email returned from signup");
        }
      } catch (error) {
        console.error("Signup failed:", error);
        trackEvent(SignUpEvent.SignUpFailed, {
          step: "implicit-free",
          error: error.message,
        });

        setStatus("error");
        setErrorMessage(
          error.message || "Error creating your account. Please try again.",
        );
        toast.error("Error creating your account. Please try again.");
      }
    };

    // Start creating the account immediately
    createAccount();
  }, []);

  if (status === "error") {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-space-y-6 tw-py-8 tw-text-center">
        <h2 className="tw-text-xl tw-font-semibold tw-text-red-600">
          Account Creation Failed
        </h2>
        <p className="tw-text-gray-600">{errorMessage}</p>
        <button
          onClick={() => setStatus("loading")}
          className="tw-bg-blue-600 tw-text-white tw-px-6 tw-py-2 tw-rounded-md tw-font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-space-y-6 tw-py-8 tw-text-center">
      <h2 className="tw-text-xl tw-font-semibold">
        Creating Your Free Account
      </h2>
      <div className="tw-flex tw-items-center tw-justify-center tw-space-x-3">
        <Loader size={28} />
        <p className="tw-text-gray-600">
          Please wait while we set up your account...
        </p>
      </div>
    </div>
  );
};

export default ImplicitCreateFreeAccountStep;
